import { ListingCarousel_ListingFragment } from "@outschool/gql-frontend-generated";
import {
  OperationVariables,
  useQueryWithPreviousData
} from "@outschool/ui-apollo";
import { ListingCarousel } from "@outschool/ui-components-website";
import {
  Loading,
  PageWidthSection
} from "@outschool/ui-legacy-component-library";
import _ from "lodash";
import React from "react";

import * as Env from "../../../shared/Env";

export const CAROUSEL_PLACEHOLDER_HEIGHT = 300;

export type FeaturedClassCarouselProps<TVariable> = {
  query: any;
  variables: TVariable;
  path: string;
  title: string | (({}) => string);
  trackingLabel: string;
  carouselRowNum: number;
  backgroundColor?: string;
  fullBleed?: boolean;
  maxListings?: number;
  batchKey?: string;
  children?: React.ReactNode;
};

export default function FeaturedClassCarousel<
  TData,
  TVariable extends OperationVariables
>({
  query,
  variables,
  path,
  title,
  trackingLabel,
  carouselRowNum,
  backgroundColor = "white",
  fullBleed = false,
  maxListings = 10,
  batchKey,
  children
}: FeaturedClassCarouselProps<TVariable>) {
  const { loading, data } = useQueryWithPreviousData<TData, TVariable>(query, {
    skip: !!Env.FEATURE_FLAGS.DISABLE_OPTIONAL_QUERIES,
    variables,
    context: { doNotRetry: true, batchKey },
    // NOTE: There's a race condition with caching when multiple FeaturedClassCarousel carousels
    // are loaded on the same page and causes infinite refetches.
    // Couldn't figure out how to fix it with caching, so I'm just disabling the cache for now.
    fetchPolicy: "no-cache"
  });
  const listings: ListingCarousel_ListingFragment[] = React.useMemo(
    () => _.take(_.get(data, path, []), maxListings),
    [data, path, maxListings]
  );
  const rowTitle = typeof title === "string" ? title : data ? title(data) : "";
  return loading ? (
    <PageWidthSection
      sx={{
        height: CAROUSEL_PLACEHOLDER_HEIGHT,
        backgroundColor,
        paddingBottom: 0,
        paddingTop: "large"
      }}
      fullBleed={fullBleed}
    >
      <Loading />
    </PageWidthSection>
  ) : listings?.length > 0 ? (
    <div>
      <ListingCarousel
        sx={{ padding: 0, backgroundColor }}
        title={rowTitle}
        listings={listings}
        trackingLabel={trackingLabel}
        carouselRowNum={carouselRowNum}
      >
        {children}
      </ListingCarousel>
    </div>
  ) : null;
}
